'use client'

import useCartStore, { ICartItem } from '@/store/cartStore'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import useWishListStore, { IWishList } from '@/store/wishListStore'

import { Heart, Minus, Plus, Trash, Zap } from 'lucide-react'
import useCommonStore from '@/store/commonStore'
import { toast } from '@/hooks/use-toast'
import Loader from '../common/loader'
import useOrderStore from '@/store/orderStore'
import { useRouter } from 'next/navigation'
import QuantityPopup from './QuantityPopup'
import { GTMAddToCartHandler, GTMAddToWishlistHandler, GTMRemoveFromCartHandler, GTMRemoveFromWishlistHandler, GTMUpdateOrderSummaryHandler, GTMViewCartHandler } from '@/lib/analytics'

type ScreenType = "cart" | "checkout"

interface IObj {
	[key: string]: any
}

export default function MyCartItems({ type, translations }: { type: ScreenType, translations: any }) {
	const { wishList, updateWishList } = useWishListStore()
	const [loading, setLoading] = useState(false)
	const [items, setItems,] = useState<{ [key: string]: any }[]>([])
	const { cartDetails, cart, updateCart } = useCartStore()
	const { isAuth } = useCommonStore()
	const router = useRouter()
	const { updateUserOrderSummary } = useOrderStore()

	const [isInWishList, setIsInWishlist] = useState(false)


	useEffect(() => {
		loadDetails()
	}, [])
	const loadDetails = async () => {
		setLoading(true)
		const result = await cartDetails(cart)
		setLoading(false)
		if (result?.status) {
			setItems(result.data?.items || [])
			let itm = result.data?.items.map((itm: any) => ({
				item_id: itm.pdt_id,
				item_variant: itm?.variant_id,
				quantity: itm.quantity,
				price: itm.spl_prc_in_num,
				item_name: itm.pdt_nm,

			}))
			GTMViewCartHandler({
				items: itm
			})
		} else {
			toast({
				title: "Error",
				description: result?.error || "Something went wrong",
				variant: "destructive"
			})
		}
	}



	const addToWishList = async (doc: IObj) => {
		const reqObj: IWishList = {
			pdt_id: doc.pdt_id,
			variant_id: doc?.variant_id || "",
			flag: !isInWishList // toggle flag
		}
		if (isInWishList){
			GTMRemoveFromWishlistHandler({
				items:[
					{
						item_id:doc.pdt_id,
						item_variant:doc?.variant_id,
						price:doc?.spl_prc_in_num,
						item_name:doc?.pdt_nm,
						item_brand:doc?.brnd_nm
						
					}
				]
			})
		}
		else{
			GTMAddToWishlistHandler({
				items:[
					{
						item_id:doc.pdt_id,
						item_variant:doc?.variant_id,
						price:doc?.spl_prc_in_num,
						item_name:doc?.pdt_nm,
						item_brand:doc?.brnd_nm
						
					}
				]
			})
		}
		const result = await updateWishList(reqObj, isAuth)
		toast({
			variant: result.status ? "default" : "destructive",
			title: result.status ? "Success" : "Error",
			description: result.status ? result.message : result.error,
		})
	};

	const cartHandler = async (item: ICartItem, mxq: number, type: string, spl_prc: number, pdt_nm: string) => {
		if (type === "increment") {
			GTMAddToCartHandler({
				value: spl_prc,
				items: [
					{
						item_id: item.pdt_id,
						item_variant: item.variant_id,
						quantity: item.quantity,
						price: spl_prc,
						item_name: pdt_nm,

					}]
			})
		}
		else if (type === "decrease") {
			GTMRemoveFromCartHandler({
				value: spl_prc,
				items: [
					{
						item_id: item.pdt_id,
						item_variant: item.variant_id,
						quantity: item.quantity,
						price: spl_prc,
						item_name: pdt_nm,

					}]
			})
		}
		const update = await updateCart(item, mxq, isAuth);
		if (update?.status) {
			toast({ variant: "default", title: "Cart updated", description: update.message });
			await loadDetails()
		} else {
			toast({ variant: "destructive", title: update?.message, description: update?.error });
		}
	};
	const updateOrderSummary = async (item: { [key: string]: any }) => {
		let itm = items.map((itm => ({
			item_id: itm.pdt_id,
			item_variant: itm?.variant_id,
			quantity: itm.quantity,
		})))
		GTMUpdateOrderSummaryHandler({
			items: itm

		})
		const result = await updateUserOrderSummary({ items: [item] })

		if (result?.status) {
			router.push("/checkout")
		} else {
			toast({
				title: "Error",
				description: result?.message || result?.error,
				variant: "destructive"
			})
		}
	}

	return (
		<div>
			<div className="text-base font-semibold mb-2">{items.length} {translations["items"] || "items"}</div>
			{
				loading ? (
					<div className='relative'>
						<Loader />
					</div>
				) : (
					<div className="flex flex-col gap-4">
						{items.map((item, index: number) => (
							<>
								<div className='bg-white rounded-xl border'>
									{/* <div> */}
									<div key={index} className=" flex items-center gap-4 px-2 pt-2">
										<Image
											src={`${item.thumbnail}?h=100`}
											alt={item.pdt_nm}
											width={60}
											height={60}
											className="rounded-md object-contain w-[60px] h-[60px]"
										/>
										<div className="flex-1">
											<div className="font-medium text-sm">{item.pdt_nm}</div>
											{!item?.in_stk && <span className="text-red-600 font-semibold">{translations["out_of_stock"] || "Out of stock"}</span>}

											<div className='flex justify-between my-2'>
												<div className="flex items-center gap-2">
													<button disabled={item.quantity <= 0} onClick={() => cartHandler({ pdt_id: item.pdt_id, variant_id: item.variant_id, quantity: item.quantity - 1 }, item.max_order_quantity, "decrease", item.spl_prc_in_num, item.pdt_nm)} className="border px-2 py-1 rounded"><Minus className='w-4 h-4' /></button>
													<span className="px-2 font-semibold">{item.quantity}</span>
													<button disabled={!item.in_stk || item.quantity >= item.max_order_quantity} onClick={() => cartHandler({ pdt_id: item.pdt_id, variant_id: item.variant_id, quantity: item.quantity + 1 }, item.max_order_quantity, "increment", item.spl_prc_in_num, item.pdt_nm)} className="border px-2 py-1 rounded disabled:opacity-50"><Plus className='w-4 h-4' /></button>
												</div>
												<div className="flex  items-center gap-2">
													<span className="font-bold text-primary text-base">
														{item.spl_prc}
													</span>
													{item.spl_prc != item.price && <span className="text-base font-medium text-gray-400 line-through">
														{item.price}
													</span>}
												</div>
											</div>

										</div>

									</div>
									{/* </div> */}
									{type === "cart" && <div className='mt-2 flex border-t'>
										<button
											type='button'
											onClick={async () => {
												await addToWishList(item)
												await cartHandler({ pdt_id: item.pdt_id, variant_id: item.variant_id, quantity: 0 }, item.max_order_quantity, "decrease", item.spl_prc, item.pdt_nm)
											}}
											className='flex-1 py-3 text-xs text-gray-400 font-semibold flex items-center gap-1 justify-center'>
											<Heart className='w-4 h-4 text-gray-400' />
											<span>{translations["wishlist"] || "Wishlist"}</span>
										</button>
										<button onClick={() => cartHandler({ pdt_id: item.pdt_id, variant_id: item.variant_id, quantity: 0 }, item.max_order_quantity, "decrease", item.spl_prc, item.pdt_nm)} className='flex-1 py-3 text-xs text-gray-400 border-x font-semibold flex items-center gap-1 justify-center'><Trash className='w-4 h-4 text-gray-400' />{translations["remove"] || "Remove"}</button>
										<button disabled={!item?.in_stk} onClick={() => updateOrderSummary({ pdt_id: item.pdt_id, variant_id: item.variant_id, quantity: item.quantity })} className='flex-1 py-3 text-xs text-gray-400 font-semibold flex items-center gap-1 justify-center disabled:opacity-40'>{item?.in_stk ? <Zap className='w-4 h-4 text-gray-400' /> : <Zap className='w-4 h-4 text-gray-400 opacity-50' />}{translations["buy_now"] || "Buy now"}</button>
									</div>}
								</div>
							</>
						))}
					</div>
				)
			}

		</div>
	)
}